// Multi-step login form. Visual parity with frontend/src/app/login/login-client.tsx.
// Steps: email → (need_payment | need_matrix | need_2fa | login).
// #46 C46-LOGIN-REGISTER-REMOVIDO: the standalone register step was removed —
// account-less visitors land on `need_matrix` (broker-side signup).
import { useEffect, useState } from "react";
import { Mail, Lock, UserPlus, ArrowLeft, RefreshCw } from "lucide-react";
import { ParticlesBackground } from "@/components/ParticlesBackground";
import {
  buildInternalCheckoutTracking,
  buildTrackedCheckoutUrl,
} from "@/features/checkout/tracking";
import type { CheckoutPlacementKey } from "@/features/checkout/placements";
import { formatCurrencyCents } from "@/lib/format/currency";
import type { LoginFlowReturn, PublicPlanView } from "../types";
import {
  FormInput,
  ErrorMessage,
  SubmitButton,
  BackButton,
} from "./LoginFormShared";
import {
  formatRateLimitClock,
  rateLimitSecondsUntil,
} from "./loginRateLimitCountdown";
import { PaymentRequiredPlanGrid } from "./PaymentRequiredPlanGrid";

const LOGIN_NO_LICENSE_PLAN_ID = "login-default";
const LOGIN_PAYMENT_FALLBACK_PLAN_ID = "login-payment-link";

// ── Email step ────────────────────────────────────────────────────────────────

interface EmailStepProps {
  flow: LoginFlowReturn;
  checkoutUrl: string;
}

function NoLicenseHint({
  checkoutUrl,
}: {
  checkoutUrl: string;
}): React.ReactElement {
  return (
    <p className="text-xs text-center text-foreground/45 flex items-center justify-center gap-3">
      <span
        className="h-px w-6 bg-gradient-to-r from-transparent via-white/15 to-transparent"
        aria-hidden="true"
      />
      <span>
        Ainda não tem licença?{" "}
        <a
          href={buildLoginCheckoutHref(
            checkoutUrl,
            LOGIN_NO_LICENSE_PLAN_ID,
            "login-no-license",
          )}
          tabIndex={-1}
          data-testid="login-comprar-cta"
          className="text-brand-cyan hover:text-brand-cyan font-semibold underline underline-offset-2 transition-colors"
        >
          Adquira aqui
        </a>
      </span>
      <span
        className="h-px w-6 bg-gradient-to-r from-transparent via-white/15 to-transparent"
        aria-hidden="true"
      />
    </p>
  );
}

function LoginEmailReadonlyField(props: {
  readonly email: string;
}): React.ReactElement {
  return (
    <div className="group relative">
      <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-brand-cyan transition-colors">
        <Mail className="h-4 w-4" />
      </div>
      <div
        aria-label="Email"
        data-testid="login-email-display"
        className="w-full select-none rounded-xl border border-brand-cyan bg-brand-cyan/10 py-3.5 pl-11 pr-4 font-mono text-sm text-brand-cyan shadow-inner shadow-black/20"
      >
        {props.email}
      </div>
    </div>
  );
}

export function EmailStep({
  flow,
  checkoutUrl,
}: EmailStepProps): React.ReactElement {
  return (
    <form
      onSubmit={(e) => void flow.handleEmailSubmit(e)}
      className="space-y-5 relative z-10"
    >
      {/* #android-s8-login-flow: no `autoFocus` — Chrome WebView ~90 on the
        Samsung S8 popped the IME immediately on mount, which scrolled the
        LoginCard below the visible viewport ("fica fora de foco o login
        total"). The operator taps the field manually when ready. */}
      <FormInput
        type="email"
        name="email"
        autoComplete="email"
        inputMode="email"
        value={flow.email}
        onChange={flow.setEmail}
        placeholder="Seu Email"
        icon={<Mail className="w-4 h-4" />}
        focusColor="cyan"
        required
      />
      {!flow.isLicenseBlocked && <NoLicenseHint checkoutUrl={checkoutUrl} />}
      {flow.loginError && <ErrorMessage message={flow.loginError} />}
      <SubmitButton
        isLoading={flow.isLoading}
        loadingText="VERIFICANDO..."
        submitText="CONTINUAR"
      />
    </form>
  );
}

// ── Login step ────────────────────────────────────────────────────────────────

interface LoginStepProps {
  flow: LoginFlowReturn;
}

function rateLimitMessage(seconds: number): string {
  return `Muitas tentativas. Tente novamente em ${formatRateLimitClock(seconds)}.`;
}

function useRateLimitRemainingSeconds(untilMs: number | null): number {
  const [remainingSeconds, setRemainingSeconds] = useState(() =>
    rateLimitSecondsUntil(untilMs),
  );
  useEffect(() => {
    setRemainingSeconds(rateLimitSecondsUntil(untilMs));
    if (untilMs === null) return;
    const timer = window.setInterval(() => {
      setRemainingSeconds(rateLimitSecondsUntil(untilMs));
    }, 250);
    return () => window.clearInterval(timer);
  }, [untilMs]);
  return remainingSeconds;
}

export function LoginStep({ flow }: LoginStepProps): React.ReactElement {
  const rateLimitSeconds = useRateLimitRemainingSeconds(flow.rateLimitUntilMs);
  const isRateLimited = rateLimitSeconds > 0;
  const submitText = isRateLimited
    ? `AGUARDE ${formatRateLimitClock(rateLimitSeconds)}`
    : "ENTRAR";
  return (
    <form
      onSubmit={(e) => {
        if (isRateLimited) {
          e.preventDefault();
          return;
        }
        void flow.handleLogin(e);
      }}
      className="space-y-5 relative z-10"
    >
      <input
        type="hidden"
        name="username"
        autoComplete="username"
        value={flow.email}
        readOnly
      />
      <LoginEmailReadonlyField email={flow.email} />
      {/* #android-s8-login-flow: see EmailStep — autoFocus on password
        triggered the same IME-induced scroll out of viewport on S8
        WebView. The user just tapped CONTINUAR, so they naturally tap
        the password field next. */}
      <FormInput
        type="password"
        name="password"
        autoComplete="current-password"
        value={flow.password}
        onChange={flow.setPassword}
        placeholder="Sua Senha"
        ariaLabel="Senha"
        role="textbox"
        icon={<Lock className="w-4 h-4" />}
        focusColor="purple"
        required
      />
      {(flow.loginError || isRateLimited) && (
        <ErrorMessage
          message={
            isRateLimited ? rateLimitMessage(rateLimitSeconds) : flow.loginError
          }
        />
      )}
      <SubmitButton
        isLoading={flow.isLoading}
        isDisabled={isRateLimited}
        loadingText="Entrando..."
        submitText={submitText}
      />
      <BackButton onClick={flow.handleBack} isDisabled={flow.isLoading} />
    </form>
  );
}

// ── Need payment step ─────────────────────────────────────────────────────────

interface PaymentStepProps {
  flow: LoginFlowReturn;
  plans: readonly PublicPlanView[];
}

function PaymentFallbackButton(props: {
  readonly href: string;
  readonly label: string;
}): React.ReactElement {
  return (
    <a
      href={props.href}
      target="_blank"
      rel="noopener noreferrer"
      className="block w-full rounded-[18px] border border-brand-cyan bg-brand-cyan px-4 py-3.5 text-center text-sm font-bold tracking-wide text-primary-foreground shadow-lg"
    >
      {props.label}
    </a>
  );
}

function PaymentStepActions(props: {
  readonly onBack: () => void;
  readonly onRetry: () => void;
  readonly isLoading: boolean;
}): React.ReactElement {
  return (
    <div className="flex gap-3">
      <button
        type="button"
        onClick={props.onBack}
        disabled={props.isLoading}
        className="flex-1 py-2.5 px-4 rounded-xl font-medium text-sm text-foreground/70 hover:text-foreground bg-surface hover:bg-surface border border-border flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ArrowLeft className="w-4 h-4" />
        Voltar
      </button>
      <button
        type="button"
        onClick={props.onRetry}
        disabled={props.isLoading}
        className="flex-1 py-2.5 px-4 rounded-xl font-medium text-sm text-foreground/70 hover:text-foreground bg-surface hover:bg-surface border border-border flex items-center justify-center gap-2 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <RefreshCw
          className={`w-4 h-4 ${props.isLoading ? "animate-spin" : ""}`}
        />
        {props.isLoading ? "Verificando..." : "Já paguei"}
      </button>
    </div>
  );
}

export function PaymentStep({
  flow,
  plans,
}: PaymentStepProps): React.ReactElement {
  const hasPlans = plans.length > 0;
  const displayUrl = flow.checkoutUrl;
  const formattedPrice = formatCurrencyCents(flow.paymentLinkPriceCents);
  const fallbackLabel = flow.paymentLinkName
    ? `COMPRAR ${flow.paymentLinkName.toUpperCase()} - ${formattedPrice}`
    : "ADQUIRIR ACESSO";
  return (
    <div className="relative z-10 space-y-6">
      <div className="rounded-[20px] border border-brand-cyan bg-card p-4">
        <p className="truncate text-sm font-medium text-foreground">
          {flow.email}
        </p>
      </div>
      {hasPlans ? (
        <PaymentRequiredPlanGrid plans={plans} displayUrl={displayUrl} />
      ) : (
        <PaymentFallbackButton
          href={buildLoginCheckoutHref(
            displayUrl,
            LOGIN_PAYMENT_FALLBACK_PLAN_ID,
            "login-payment-fallback",
          )}
          label={fallbackLabel}
        />
      )}
      <PaymentStepActions
        onBack={flow.handleBack}
        onRetry={flow.handleRetry}
        isLoading={flow.isLoading}
      />
    </div>
  );
}

function buildLoginCheckoutHref(
  checkoutUrl: string,
  planId: string,
  placementKey: CheckoutPlacementKey,
): string {
  return buildTrackedCheckoutUrl(
    checkoutUrl,
    buildInternalCheckoutTracking(planId, placementKey),
  );
}

// ── Need Matrix step ──────────────────────────────────────────────────────────

interface MatrixStepProps {
  flow: LoginFlowReturn;
}

function MatrixStepIntro({ email }: { email: string }): React.ReactElement {
  return (
    <>
      <div className="text-center space-y-3">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-cyan/10">
          <UserPlus className="h-8 w-8 text-brand-cyan" />
        </div>
        <h2 className="text-xl font-bold text-foreground">
          Crie sua Conta na Matrix
        </h2>
        <p className="text-sm text-foreground/60">
          Você já tem acesso ao bot! Agora crie sua conta na corretora Matrix
          para começar.
        </p>
      </div>
      <div className="space-y-2 rounded-xl border border-border bg-surface p-4 shadow-inner backdrop-blur-sm">
        <p className="text-xs text-foreground/50">
          Use o mesmo email para criar sua conta:
        </p>
        <p className="text-sm text-brand-cyan font-medium truncate">{email}</p>
      </div>
    </>
  );
}

function MatrixStepActions(props: {
  readonly onBack: () => void;
  readonly onRetry: () => void;
  readonly isLoading: boolean;
}): React.ReactElement {
  return (
    <div className="flex gap-3">
      <button
        type="button"
        onClick={props.onBack}
        disabled={props.isLoading}
        className="flex-1 py-2.5 px-4 rounded-xl font-medium text-sm text-foreground/70 hover:text-foreground bg-surface hover:bg-surface border border-border flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ArrowLeft className="w-4 h-4" />
        Voltar
      </button>
      <button
        type="button"
        onClick={props.onRetry}
        disabled={props.isLoading}
        className="flex-1 py-2.5 px-4 rounded-xl font-medium text-sm text-foreground/70 hover:text-foreground bg-surface hover:bg-surface border border-border flex items-center justify-center gap-2 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <RefreshCw
          className={`w-4 h-4 ${props.isLoading ? "animate-spin" : ""}`}
        />
        {props.isLoading ? "Verificando..." : "Já criei"}
      </button>
    </div>
  );
}

export function MatrixStep({ flow }: MatrixStepProps): React.ReactElement {
  return (
    <div className="space-y-6 relative z-10">
      <MatrixStepIntro email={flow.email} />
      <a
        href={flow.createMatrixUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="block w-full rounded-xl bg-brand-cyan px-4 py-3.5 text-center text-sm font-bold tracking-wide text-primary-foreground shadow-lg hover:bg-brand-cyan/90"
      >
        CRIAR CONTA NA MATRIX
      </a>
      <MatrixStepActions
        onBack={flow.handleBack}
        onRetry={flow.handleRetry}
        isLoading={flow.isLoading}
      />
      <p className="text-[11px] text-center text-foreground/40 flex items-center justify-center gap-3">
        <span
          className="h-px w-6 bg-gradient-to-r from-transparent via-white/10 to-transparent"
          aria-hidden="true"
        />
        <span>
          Após criar sua conta, clique em &quot;Já criei&quot; para continuar
        </span>
        <span
          className="h-px w-6 bg-gradient-to-r from-transparent via-white/10 to-transparent"
          aria-hidden="true"
        />
      </p>
    </div>
  );
}

// #46 C46-LOGIN-REGISTER-REMOVIDO: the standalone `RegisterStep` was removed.
// Visitors without an account are routed to `MatrixStep` (broker-side signup).

export { ParticlesBackground };
